import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiUpload } = FiIcons;

const modalVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.8,
    transition: {
      duration: 0.2
    }
  }
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 }
};

const emojiGridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.02
    }
  }
};

const emojiVariants = {
  hidden: { opacity: 0, scale: 0 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20
    }
  },
  hover: {
    scale: 1.2,
    transition: {
      duration: 0.1
    }
  }
};

const EditChildModal = ({ child, isOpen, onClose }) => {
  const { updateChild, deleteChild } = useAppContext();
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    avatarEmoji: '😊'
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const emojis = [
    '😊', '😎', '🤗', '😍', '🥰', '😄', '🤩', '🤯',
    '😇', '🤓', '😋', '🤭', '😁', '😃', '😆', '🥳',
    '🦄', '🌸', '🏠', '🎯', '👑', '🦋', '🎸', '🥕',
    '🌍', '🏀', '🏈', '💎', '🎭', '🎪', '💫', '🎯'
  ];

  useEffect(() => {
    if (child) {
      setFormData({
        name: child.name,
        age: child.age.toString(),
        avatarEmoji: child.avatarEmoji
      });
      setShowDeleteConfirm(false);
    }
  }, [child]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    updateChild(child.id, {
      ...child,
      name: formData.name,
      age: parseInt(formData.age) || 0,
      avatarEmoji: formData.avatarEmoji
    });
    onClose();
  };

  const handleDelete = () => {
    deleteChild(child.id);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto"
        style={{ zIndex: 9999 }}
        onClick={onClose}
      >
        <motion.div
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto my-4 relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Edit Child Profile</h2>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </motion.button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Current Avatar */}
            <div className="text-center">
              <motion.div 
                whileHover={{ rotate: 5, scale: 1.1 }}
                className="w-20 h-20 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-2"
              >
                {formData.avatarEmoji}
              </motion.div>
              <p className="text-sm text-gray-600">Current Avatar</p>
            </div>

            {/* Name Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Child's Name *
              </label>
              <motion.input
                whileFocus={{ scale: 1.02 }}
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                required
              />
            </div>

            {/* Age Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Age (optional)
              </label>
              <motion.input
                whileFocus={{ scale: 1.02 }}
                type="number"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                min="0"
                max="18"
              />
            </div>

            {/* Avatar Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Choose Avatar
              </label>
              
              {/* Tab Headers */}
              <div className="flex border-b border-gray-200 mb-4">
                <button
                  type="button"
                  className="flex-1 py-2 px-4 text-sm font-medium text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                >
                  🙂 Emoji
                </button>
                <button
                  type="button"
                  className="flex-1 py-2 px-4 text-sm font-medium text-gray-500 border-b-2 border-transparent cursor-not-allowed opacity-50"
                  disabled
                >
                  <SafeIcon icon={FiUpload} className="inline mr-1" />
                  Upload
                </button>
              </div>

              {/* Emoji Grid */}
              <motion.div 
                variants={emojiGridVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-8 gap-2 max-h-40 overflow-y-auto"
              >
                {emojis.map((emoji, index) => (
                  <motion.button
                    key={emoji}
                    variants={emojiVariants}
                    whileHover="hover"
                    whileTap={{ scale: 0.9 }}
                    type="button"
                    onClick={() => setFormData({ ...formData, avatarEmoji: emoji })}
                    className={`w-10 h-10 text-xl rounded-lg border-2 transition-all ${
                      formData.avatarEmoji === emoji
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    {emoji}
                  </motion.button>
                ))}
              </motion.div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </motion.button>
            </div>

            {/* Delete Section */}
            <div className="border-t pt-4 mt-6">
              <AnimatePresence>
                {!showDeleteConfirm ? (
                  <motion.button
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    Delete Child Profile
                  </motion.button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-3"
                  >
                    <p className="text-sm text-red-600 text-center">
                      Are you sure? This action cannot be undone.
                    </p>
                    <div className="flex space-x-3">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="button"
                        onClick={handleDelete}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        🗑️ Delete
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EditChildModal;